import {Injectable} from "@angular/core";
import {HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest} from "@angular/common/http";
import {IdentityService} from "../services/identity.service";
import {SessionRefreshError} from "../services/session-refresh-error";
import {IDENTITY_TOKEN} from "../constants/identity.constants";
import {catchError, Observable, switchMap, throwError} from "rxjs";

@Injectable()
export class AuthJwtTokenInterceptor implements HttpInterceptor {

  constructor(private identityService: IdentityService) { }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const sentToken = this.identityService.authToken;
    return next.handle(this.applyToken(request)).pipe(
      catchError((error: HttpErrorResponse) => {
        // The token request itself is never refreshed: its 401 would wait for the very refresh it belongs to.
        if (error.status !== 401 || request.url === IDENTITY_TOKEN) {
          return throwError(() => error);
        }
        // The token changed while this request was in flight: another request has already refreshed it (or the session
        // has ended) — no second refresh.
        if (sentToken && this.identityService.authToken !== sentToken) {
          return this.resendWithCurrentToken(request, next);
        }
        if (!this.identityService.userSession.getValue()?.refresh_token) {
          return throwError(() => error);
        }
        // The rule for a failed refresh (leave the session or stay and show one message) lives in IdentityService.
        return this.identityService.refreshToken().pipe(
          switchMap(() => next.handle(this.applyToken(request)))
        );
      })
    );
  }

  // With the session gone there is nothing to send with, and the user has already been told by the refresh.
  private resendWithCurrentToken(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return this.identityService.authToken
      ? next.handle(this.applyToken(request))
      : throwError(() => new SessionRefreshError());
  }

  private applyToken(request: HttpRequest<any>): HttpRequest<any> {
    return this.identityService.authToken ? request.clone({
      setHeaders: {
        Authorization: `Bearer ${this.identityService.authToken}`,
        ContentType: 'application/json',
      }
    }) : request;
  }
}
